// ─────────────────────────────────────────────────────────────────────────────
// Internationalisation — namespaced, typed, and split by feature.
//
// v1 kept ~1,700 keys in a single 1,900-line file, which nobody could safely
// edit and which several screens simply bypassed. Here each feature owns its own
// dictionary, the key type is derived from the English dictionary, and a missing
// or misspelled key is a COMPILE ERROR rather than a stray "leave.request.title"
// rendered to a user.
// ─────────────────────────────────────────────────────────────────────────────

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode, type ReactElement, useEffect } from "react";
import { common } from "./common";
import { dayCountParts } from "./days";
import { employees } from "./employees";
import { contracts } from "./contracts";
import { loans } from "./loans";
import { documents } from "./documents";
import { stamp } from "./stamp";
import { auth } from "./auth";
import { payroll } from "./payroll";
import { leave } from "./leave";
import { requests } from "./requests";
import { dashboard } from "./dashboard";
import { attendance } from "./attendance";
import { mail } from "./mail";
import { performance } from "./performance";
import { tasks } from "./tasks";
import { settings } from "./settings";
import { reports } from "./reports";
import { orgchart } from "./orgchart";
import { ai } from "./ai";
import { enums } from "./enums";

export type Locale = "en" | "ar";

export const DICTIONARIES = { common, auth, employees, contracts, loans, documents, payroll, leave, requests, dashboard, attendance, mail, performance, tasks, settings, reports, orgchart, ai, stamp, enums } as const;

type Namespace = keyof typeof DICTIONARIES;

/** Every valid key, as `namespace.key`. Derived, never hand-maintained. */
export type TranslationKey = {
  [N in Namespace]: `${N}.${Extract<keyof (typeof DICTIONARIES)[N]["en"], string>}`;
}[Namespace];

interface I18nValue {
  readonly locale: Locale;
  readonly dir: "ltr" | "rtl";
  readonly setLocale: (locale: Locale) => void;
  /** Translates a key, interpolating {placeholders}. */
  readonly t: (key: TranslationKey, values?: Readonly<Record<string, string | number>>) => string;
  /** Picks the right field from a bilingual record. */
  readonly pick: (en: string | null | undefined, ar: string | null | undefined) => string;
  /** A number of days, agreeing with the locale's plural rules. */
  readonly days: (n: number) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

const STORAGE_KEY = "alba.locale";

function readStoredLocale(): Locale {
  const stored = typeof window === "undefined" ? null : window.localStorage.getItem(STORAGE_KEY);
  return stored === "ar" ? "ar" : "en";
}

export function I18nProvider({ children }: { children: ReactNode }): ReactElement {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);

  // Applies the stored locale to the document on MOUNT, not only when the
  // toggle is clicked. `setLocale` below sets `lang`/`dir` as a side effect of
  // switching, which meant a reload with Arabic already in localStorage
  // rendered Arabic text inside an ltr document: the shell never mirrored, so
  // the sidebar stayed on the left and logical properties resolved the wrong
  // way round. The direction has to follow the locale however it was arrived at.
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
  }, [locale]);

  const setLocale = useCallback((next: Locale): void => {
    setLocaleState(next);
    window.localStorage.setItem(STORAGE_KEY, next);
    document.documentElement.lang = next;
    document.documentElement.dir = next === "ar" ? "rtl" : "ltr";
  }, []);

  const t = useCallback(
    (key: TranslationKey, values?: Readonly<Record<string, string | number>>): string => {
      const separator = key.indexOf(".");
      const namespace = key.slice(0, separator);
      const name = key.slice(separator + 1);

      // `key` is a TranslationKey, so the namespace is always one of the five.
      // The lookup is still written defensively because `t` is the one function
      // every screen depends on, and a thrown error here would blank the page.
      const dict: (typeof DICTIONARIES)[Namespace] | undefined =
        Object.prototype.hasOwnProperty.call(DICTIONARIES, namespace)
          ? DICTIONARIES[namespace as Namespace]
          : undefined;
      if (dict === undefined) return key;

      const table: Readonly<Record<string, string>> = dict[locale];
      const fallback: Readonly<Record<string, string>> = dict.en;
      let text = table[name] ?? fallback[name] ?? key;

      if (values !== undefined) {
        for (const [placeholder, value] of Object.entries(values)) {
          text = text.replace(new RegExp(`\\{${placeholder}\\}`, "g"), String(value));
        }
      }
      return text;
    },
    [locale],
  );

  const pick = useCallback(
    (en: string | null | undefined, ar: string | null | undefined): string =>
      (locale === "ar" ? ar ?? en : en ?? ar) ?? "",
    [locale],
  );

  // Plural agreement — see ./days.ts for why this is not `${n} ${t("days")}`.
  const days = useCallback(
    (n: number): string => {
      const { key, n: shown } = dayCountParts(n, locale);
      return t(key, { n: shown });
    },
    [locale, t],
  );

  const value = useMemo<I18nValue>(
    () => ({ locale, dir: locale === "ar" ? "rtl" : "ltr", setLocale, t, pick, days }),
    [locale, setLocale, t, pick, days],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (value === null) throw new Error("useI18n must be used inside <I18nProvider>.");
  return value;
}
